import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { newPasswordField, PASSWORD_MIN_LENGTH } from '@/entities/viewer';
import { FormError, useFieldError, useServerErrors } from '@/shared/forms';
import { Button, TextField } from '@/shared/ui';

import { useChangePassword } from './api';
import styles from './AccountForms.module.css';

const schema = z
  .object({
    current_password: z.string().min(1, 'REQUIRED'),
    new_password: newPasswordField,
    confirm_password: z.string(),
  })
  .refine((v) => v.new_password === v.confirm_password, {
    path: ['confirm_password'],
    message: 'PASSWORD_MISMATCH',
  });
type Values = z.infer<typeof schema>;
const FIELDS = ['current_password', 'new_password'] as const;

export function PasswordForm() {
  const { t } = useTranslation();
  const fieldError = useFieldError();
  const change = useChangePassword();
  const [saved, setSaved] = useState(false);
  const { register, handleSubmit, formState, setError, reset } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { current_password: '', new_password: '', confirm_password: '' },
  });
  const serverErrors = useServerErrors(setError, FIELDS);

  const onSubmit = handleSubmit(({ current_password, new_password }) => {
    serverErrors.clear();
    setSaved(false);
    change.mutate(
      { current_password, new_password },
      {
        onSuccess: () => {
          reset();
          setSaved(true);
        },
        onError: serverErrors.handle,
      },
    );
  });

  return (
    <form className={styles.form} onSubmit={(e) => void onSubmit(e)} noValidate>
      <FormError error={serverErrors.formError} />
      <TextField
        label={t('account.currentPassword')}
        type="password"
        autoComplete="current-password"
        error={fieldError(formState.errors.current_password?.message)}
        {...register('current_password')}
      />
      <TextField
        label={t('account.newPassword')}
        type="password"
        autoComplete="new-password"
        hint={t('auth.passwordHint', { count: PASSWORD_MIN_LENGTH })}
        error={fieldError(formState.errors.new_password?.message)}
        {...register('new_password')}
      />
      <TextField
        label={t('account.confirmPassword')}
        type="password"
        autoComplete="new-password"
        error={fieldError(formState.errors.confirm_password?.message)}
        {...register('confirm_password')}
      />
      <div className={styles.actions}>
        <Button type="submit" variant="primary" disabled={change.isPending}>
          {t('account.changePassword')}
        </Button>
        {saved && (
          <span className={styles.success} role="status">
            {t('account.passwordChanged')}
          </span>
        )}
      </div>
    </form>
  );
}
